import { createReducer, on } from '@ngrx/store';
import {
  ApiEndpointType,
  amountTypeDict,
  checkRecipientTypeDict,
  transactionTypes, CheckAbbreviatedRecipientTypes
} from '../../shared/utilities/Constants';
import {
  StandingAuthAchActions,
  StandingAuthCheckActions,
  StandingAuthConfirmationPageActions,
  StandingAuthDetailsActions,
  StandingAuthFormActions,
  StandingAuthInternalTransferActions,
  StandingAuthReviewPageActions,
} from '../actions';
import {
  updateFormStatus,
  updateFormValue,
} from '../actions/standing-authorizations-form.action';
import {
  AchStandingAuthDetails,
  AchStandingAuthForm,
  CheckStandingAuthDetails,
  StandingAuthFormServiceError,
  StandingAuthorizationForm,
  WireStandingAuthDetails,
  isAchFormSA, ItStandingAuthDetails
} from '../models/standingAuthorizationForm';
import { StandingAuthWireActions } from '../actions';
import { FormatPercentPipe } from '../../shared/pipes/FormatPercentPipe';
import { FormatDatePipe } from '../../shared/pipes/FormatDatePipe';
import { ConfirmationPageActions, StandingAuthorizationsActions } from '../../create-request/actions';
import { SharedActions } from '../../shared/actions';
import {InitReleaseActions} from "../../initiate-and-release/actions";
import { DraftListAttributes } from '../../initiate-and-release/Models/Drafts/DraftResponse';

export const standingAuthFormKey = 'standingAuthorizationForm';

export type State = StandingAuthorizationForm;

export const initialStandingAuthFormServiceError: StandingAuthFormServiceError = {
  status: 0,
  url: '',
  name: '',
  message: '',
  correlationId: '',
  hasError: false,
  errorList: []
}

export const initialStandingAuthFormState: State = {
  loading: false,
  error: '',
  hasError: false,
  transactionType: '',
  valid: false,
  isSubmitAndCreateBundledSa: false,
  standingAuthorizationDetails: {},
  serviceError: initialStandingAuthFormServiceError,
  prefilledFlow: {
    state: '',
    name: ''
  },
  acknowledgeCallV3Failed: false
};

export const standingAuthorizationReducer = createReducer(
  initialStandingAuthFormState,
  on(StandingAuthReviewPageActions.cancelClicked,
    StandingAuthConfirmationPageActions.createNewStandingAuthClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    InitReleaseActions.getInitReleaseList,
    () => {
      return initialStandingAuthFormState;
    }),
  on(SharedActions.cancelModalContinueClicked, (state) => {
    return {
      ...state,
      prefilledFlow: {
        state: '' as const,
        name: '' as const
      }
    }
  }),
  on(updateFormValue, (state, { type, ...update }) => {
    return { ...state, ...update };
  }),
  on(updateFormStatus, (state, { type, ...valid }) => {
    return { ...state, ...valid };
  }),
  on(StandingAuthCheckActions.callOneTimeInput,
    StandingAuthCheckActions.callVerify, (state) => {
      return {
        ...state,
        loading: true,
        error: '',
        hasError: false,
        serviceError: initialStandingAuthFormServiceError
      };
    }),
  on(StandingAuthCheckActions.inputCallSuccessful, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: initialStandingAuthFormServiceError
    };
  }),
  on(StandingAuthCheckActions.inputCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkInput,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(StandingAuthCheckActions.inquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkInquiry,
        message: err.message,
        hasError: !err.ok
      }
    }
  }),
  on(StandingAuthAchActions.inquiryCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.moneylinkInquiry,
        message: err.message,
        hasError: !err.ok
      },
    };
  }),
  on(StandingAuthCheckActions.verifyCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkVerify,
        message: err.error?.errors[0]?.detail ?? err.message,
        hasError: !err.ok
      },
    }
  }),
  on(StandingAuthAchActions.acknowledgeCallFail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.moneylinkAcknowledge,
        message: err.message,
        hasError: !err.ok,
        errorList: err.error?.errors ?? []
      },
    }
  }),
  on(StandingAuthCheckActions.acknowledgeCallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.checkAcknowledge,
        message: err.message,
        hasError: !err.ok,
        errorList: err.error?.errors ?? []
      },
    }
  }),
  on(StandingAuthInternalTransferActions.acknowledgeCallFail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferAcknowledge,
        message: err.message,
        hasError: !err.ok,
        errorList: err.error?.errors ?? [],
      },
    }
  }),
  on(StandingAuthWireActions.acknowledgeCallV3Fail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireAcknowledgeV3,
        message: err.message,
        hasError: !err.ok,
        errorList: err.error?.errors ?? [],
        
      },
      acknowledgeCallV3Failed: true
    }
  }),
  on(StandingAuthWireActions.callWireInputV3,
    StandingAuthInternalTransferActions.callInternalTransferVerify,
    StandingAuthAchActions.callAchVerify,
    StandingAuthWireActions.callWiresVerifyV3, (state) => {
      return {
        ...state,
        loading: true,
        hasError: false,
        error: '',
      }
    }),
  on(StandingAuthInternalTransferActions.verifyCallSuccessful,
    StandingAuthInternalTransferActions.verifyCallFinishedWithError,
    StandingAuthInternalTransferActions.loadInternalTransferReviewPageWithoutVerifyCall,
    StandingAuthInternalTransferActions.acknowledgeCallSuccessful,
    StandingAuthCheckActions.verifyCallSuccessful,
    StandingAuthCheckActions.verifyCallFinishedWithError,
    StandingAuthWireActions.verifyV3CallSuccessful,
    StandingAuthWireActions.loadWiresReviewPageWithoutVerifyCall,
    StandingAuthCheckActions.loadChecksReviewPageWithoutVerifyCall,
    StandingAuthAchActions.verifyCallSuccessful,
    StandingAuthAchActions.verifyCallFinishedWithError,
    StandingAuthAchActions.verifyCallFailed,
    StandingAuthWireActions.verifyV3CallFailed,
    StandingAuthorizationsActions.loadSloasSuccess,
    StandingAuthorizationsActions.loadSloasFailure, (state) => {
      return {
        ...state,
        serviceError: initialStandingAuthFormServiceError,
        loading: false,
        hasError: false,
        error: '',
      }
    }),
  
  // ToDo: map correct values and remove hardcoded values with SAC-347346, SAC-347345 and SAC-347344
  on(StandingAuthWireActions.wireInputCallSuccessfulV3, (state, response) => {
    return {
      ...state,
      serviceError: initialStandingAuthFormServiceError,
      loading: false,
      hasError: false,
      error: '',
      standingAuthDetails: {
        ...state.standingAuthorizationDetails,


        receivingAccountHolderName1:
          response.sloaInfo?.beneficiary?.mailingDetails?.namePartOne,
        receivingAccountHolderName2:
          response.sloaInfo?.beneficiary?.mailingDetails?.namePartTwo,
        receivingAccountHolderAddress1:
          response.sloaInfo?.beneficiary?.mailingDetails?.addressLineOne.trim(),
        receivingAccountHolderAddress2:
          response.sloaInfo?.beneficiary?.mailingDetails?.addressLineTwo.trim(),
        receivingAccountHolderCountry:
          response.sloaInfo?.beneficiary?.mailingDetails?.countryCode,
      } as WireStandingAuthDetails
    }
  }),
  on(StandingAuthorizationsActions.loadSloasFailure, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireInputV3,
        message: err.message,
        hasError: !err.ok
      },
    }
  }),
  on(StandingAuthInternalTransferActions.clearStandingAuthInternalTransferResponse, (state) => {
    return {
      ...state,
      serviceError: initialStandingAuthFormServiceError,
    };
  }),

  on(StandingAuthWireActions.wireInputFailedV3, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireInputV3,
        message: err.message,
        hasError: !err.ok
      },
    }
  }),
  on(StandingAuthWireActions.verifyV3CallFailed, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireVerifyV3,
        message: err.message,
        hasError: !err.ok,
        errorList: err.error.errors
      },
    }
  }),
  on(StandingAuthWireActions.callWireDetails, state => {
    return {
      ...state,
      serviceError: {
        ...state.serviceError,
        status: 0,
        url: '',
        name: '',
        message: '',
        hasError: false
      }
    }
  }),
  on(StandingAuthWireActions.wireDetailsCallFailed, (state, err) => {
    return {
      ...state,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.wireDetails,
        message: err.message,
        hasError: !err.ok
      }
    }
  }),
  on(StandingAuthInternalTransferActions.verifyCallFail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferVerify,
        message: err.message,
        hasError: !err.ok,
      },
    };
  }),
  on(
    StandingAuthInternalTransferActions.loadToAccountSuccess,
    StandingAuthInternalTransferActions.loadToAccountFinishedWithError,
    (state) => {
      return {
        ...state,
        serviceError: initialStandingAuthFormServiceError,
        loading: false,
        hasError: false,
        error: '',
      };
    }
  ),
  on(StandingAuthInternalTransferActions.loadToAccountFail, (state, err) => {
    return {
      ...state,
      loading: false,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.internalTransferInput,
        message: err.message,
        hasError: !err.ok,
      },
    };
  }),
  on(StandingAuthReviewPageActions.editClicked, (state) => {
    if (state.transactionType == 'ach') {
      return {
        ...state,
        prefilledFlow: {
          state: 'started' as const,
          name:
            state.prefilledFlow.name !== ('edit-table' as const)
              ? ('edit-review' as const)
              : state.prefilledFlow.name,
        },
      };
    } else {
      return {
        ...state,
        prefilledFlow: {
          state: 'inProgress' as const,
          name:
            state.prefilledFlow.name !== ('edit-table' as const)
              ? ('edit-review' as const)
              : state.prefilledFlow.name,
        },
      };
    }
  }),
  on(StandingAuthReviewPageActions.editCompleted, (state) => {
    return {
      ...state,
      prefilledFlow: {
        ...state.prefilledFlow,
        state: 'complete' as const,
      }
    }
  }),
  on(StandingAuthFormActions.transactionTypeChanged, (state, { transactionType }) => {
    return {
      ...state,
      createStandingAuthInProgress: !!transactionType
    }
  }),
  on(StandingAuthDetailsActions.loadWithdrawalDateSuccess, (state, value) => {
    if (isAchFormSA(state)) {
      let finalState = {
        ...state,
        standingAuthorizationDetails: {
          ...state.standingAuthorizationDetails,
          withdrawalDate: value.initiationDate
        }
      } as AchStandingAuthForm;
      if (state.extraPrompts.transferDirection === 'incoming') {
        //Will need settlementDate to send to cashiering(ProcessMoneyLinkSI -1 day)
        finalState.standingAuthorizationDetails.settlementDate = value.settlementDate;
        finalState.standingAuthorizationDetails.fundsAvailableToTrade = value.fundsCreditDate;
      }
      return finalState;
    }
    return { ...state };
  }),
  on(StandingAuthAchActions.callAchInquiry,
    StandingAuthorizationsActions.loadSloas,
    StandingAuthCheckActions.callCheckInquiry, (state) => {
    return {
      ...state,
      loading: true,
      hasError: false,
      error: '',
    };
  }),
  on(StandingAuthCheckActions.inquiryCallSuccessful, (state, response) => {
    const percentFormatPipe = new FormatPercentPipe();
    let { checkInquiryResponse } = response;
    let { cashieringPayeeDetails } = checkInquiryResponse.cashieringCheckInputDetails;
    let { cashieringAddressDetails } = checkInquiryResponse.cashieringCheckInputDetails;
    let { cashieringCheckTransaction } = checkInquiryResponse.cashieringCheckInputDetails;
    const isRecipientAccountHolder = checkInquiryResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.recipientAddressType === 'addressofrecord';
    const isThirdPartyInternational = checkInquiryResponse.cashieringCheckInputDetails?.cashieringAddressDetails?.recipientAddressType === 'addressforeign';
    return {
      ...state,
      loading: false,
      serviceError: initialStandingAuthFormServiceError,
      prefilledFlow: {
        state: 'started' as const,
        name: 'edit-table' as const
      },
      standingAuthorizationDetails: {
        ...state.standingAuthorizationDetails,
        taxWithholdingsFormGroup: {
          ...state.standingAuthorizationDetails.taxWithholdingsFormGroup,
          distributionReason: checkInquiryResponse.distributionReasonDescription,
          fedTaxPercent: cashieringCheckTransaction.iraTxn ? percentFormatPipe.transform(checkInquiryResponse.fedTaxRate.toString()) : '',
          stateTaxPercent: cashieringCheckTransaction.iraTxn ? percentFormatPipe.transform(checkInquiryResponse.stateTaxRate.toString()) : '',
        },
        payeeName: cashieringPayeeDetails.payeeDetails.payeeName1,
        payeeType: checkInquiryResponse.isThirdParty ? 'thirdParty' : 'firstParty',
        recipient: checkRecipientTypeDict[cashieringAddressDetails.recipientAddressType as keyof typeof checkRecipientTypeDict],
        checkReason: checkInquiryResponse?.withdrawalReasonCode,
        checkMemo: checkInquiryResponse.memo,
        nickname: checkInquiryResponse.nickName,
        useConstructedPayee: cashieringPayeeDetails.useConstructedPayee,
        payee: [
          checkInquiryResponse.cashieringCheckInputDetails.cashieringPayeeDetails.payeeDetails.payeeName1,
          checkInquiryResponse.cashieringCheckInputDetails.cashieringPayeeDetails.payeeDetails.payeeName2 ?? '',
          checkInquiryResponse.cashieringCheckInputDetails.cashieringPayeeDetails.payeeDetails.payeeName3 ?? ''
        ],
        distributionReason: checkInquiryResponse.distributionReasonDescription,
        addresseeName: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[0]?.mailName ?? '',
        addresseeNameCont: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[1]?.mailName ?? '',
        addresseeNameCont2: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.mailAddressDetails[2]?.mailName ?? '',
        streetAddress: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.addressLine1 ?? '',
        streetAddressCont: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.addressLine2 ?? '',
        city: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.city ?? '',
        zipCode: (isRecipientAccountHolder || isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim() !== ''
          ? checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim()?.concat('-', checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim())
          : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim(),
        state: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.stateCode,
        country: (isRecipientAccountHolder || !isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.countryCode,
        stateProvince: isRecipientAccountHolder ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.stateName,
        postalCode: (isRecipientAccountHolder || !isThirdPartyInternational) ? '' : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim() !== ''
          ? checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim()?.concat('-', checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipSuffCode?.trim())
          : checkInquiryResponse?.cashieringCheckInputDetails?.cashieringAddressDetails?.addressDetails?.zipCode?.trim(),
      } as CheckStandingAuthDetails,
    }
  }),
  on(StandingAuthAchActions.inquiryCallSuccessful, (state, response) => {
    const percentFormatPipe = new FormatPercentPipe();
    const dateFormatPipe = new FormatDatePipe();
    return {
      ...state,
      loading: false,
      serviceError: initialStandingAuthFormServiceError,
      prefilledFlow: {
        state: 'started' as const,
        name: 'edit-table' as const
      },
      standingAuthorizationDetails: {
        ...state.standingAuthorizationDetails,
        /*
          TODO: Withdrawal date received from Inquiry call is sometimes incorrect (doesn't take into account federal holidays)
        */
        recurringFrequency: response.achInquiryResponse.frequency,
        endDate: response.achInquiryResponse.endDate ? dateFormatPipe.transform(response.achInquiryResponse.endDate ?? '') : '',
        secondProcessDate: response.achInquiryResponse.secondProcessDate ? dateFormatPipe.transform(response.achInquiryResponse.secondProcessDate ?? '') : '',
        withdrawalDate: response.achInquiryResponse.nextProcessDate ? dateFormatPipe.transform(response.achInquiryResponse.nextProcessDate ?? '') : '',
        completionDate: dateFormatPipe.transform(response.achInquiryResponse.processDate ?? ''),
        amount: response.achInquiryResponse.grossNetIndicator === 'G' ? response.achInquiryResponse.grossAmt : response.achInquiryResponse.netAmt,
        amountType: response.achInquiryResponse.grossNetIndicator === 'N' ? amountTypeDict.net : amountTypeDict.gross,
        contributionReason: response.achInquiryResponse.contributionReason,
        trustIncomePercent: response.achInquiryResponse.trustFields.isTrust ? percentFormatPipe.transform(response.achInquiryResponse.trustFields.trustIncmPct) : '',
        trustPrincipalPercent: response.achInquiryResponse.trustFields.isTrust ? percentFormatPipe.transform(response.achInquiryResponse.trustFields.trustPrinPct) : '',
        trustTaxCode: response.achInquiryResponse.trustFields.isTrust ? response.achInquiryResponse.trustFields.trustTaxCode : '',
        trustPaidFor: response.achInquiryResponse.trustFields.paidFor,
        trustComment: response.achInquiryResponse.trustFields.tranMemo,
        taxWithholdingsFormGroup: {
          ...state.standingAuthorizationDetails.taxWithholdingsFormGroup,
          distributionReason: response.achInquiryResponse.iraFields.isIraTransaction ? response.achInquiryResponse.distributionReasonDescription : '',
          fedTaxPercent: response.achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(response.achInquiryResponse.iraFields.fedTaxPercentage) : '',
          stateTaxPercent: response.achInquiryResponse.iraFields.isIraTransaction ? percentFormatPipe.transform(response.achInquiryResponse.iraFields.stateTaxPercentage) : '',
        },
      } as AchStandingAuthDetails,
    };
  }),
  on(InitReleaseActions.initReleaseByDraftIdSuccessful, (state, { response, isSloa } ) => {
    if (!isSloa) return state;
    if (response.transactionType === transactionTypes.wire){
      return mapWireSloaDetailsFromInitRelease(response, state);
    }
    if (response.transactionType === 'journal'){
      return mapJournalSloaDetailsFromInitRelease(response, state);
    }
    if (response.transactionType === transactionTypes.check){
      return mapCheckSloaDetailsFromInitRelease(response, state);
    }
    return state;
  }),
  on(InitReleaseActions.initReleaseByDraftIdFailed, (state, err) => {
    return {
      ...state,
      serviceError: {
        ...state.serviceError,
        status: err.status,
        url: err.url,
        name: ApiEndpointType.initReleaseByDraftId,
        message: err.message,
        hasError: !err.ok
      }
    }
  }),
  on(
    StandingAuthFormActions.initializeServiceError,
    (state) => {
      return {
        ...state,
        serviceError: initialStandingAuthFormServiceError,
      };
    }
  ),
);

function mapCheckSloaDetailsFromInitRelease(response: DraftListAttributes, state: State): StandingAuthorizationForm {
  let draftObject = response.draftObject;
  let isThirdPartyRecipient: boolean = response.draftObject.recipientAddressType == 'addressdomestic';
  return {
    ...state,
    transactionType: response.transactionType as transactionTypes,
    isSubmitAndCreateBundledSa: draftObject.bSLOATxn,
    standingAuthorizationDetails: {
      ...state.standingAuthorizationDetails,
      authorizationNickname: draftObject.nickname,
      recipient: checkRecipientTypeDict[response.draftObject.recipientAddressType as keyof typeof checkRecipientTypeDict] as CheckAbbreviatedRecipientTypes,
      modifiedPayeeName1: draftObject.payeeDetails?.payeeName1 ?? '',
      modifiedPayeeName2: '',
      modifiedPayeeName3: '',
      modifiedPayeeCheckbox: draftObject.isFirstPartyModified as boolean,
      payeeType: draftObject.isThirdParty ? 'thirdParty' : 'firstParty',
      checkMemo: response.draftObject.memo ?? '',
      payeeName: draftObject.isFirstPartyModified ? draftObject.payeeDetails?.payeeName1 : draftObject.payeeLine1 ? draftObject.payeeLine1 : draftObject.payeeDetails?.payeeName1,
      additionalPayeeName1: draftObject.payeeLine2 ?? '',
      additionalPayeeName2: draftObject.payeeLine3 ?? '',
      addresseeName: isThirdPartyRecipient ? draftObject.otherDomesticmailTo1 : draftObject.otherForeginmailTo1,
      addresseeNameCont: isThirdPartyRecipient ? draftObject.otherDomesticmailTo2 : draftObject.otherForeginmailTo2,
      addresseeNameCont2: isThirdPartyRecipient ? draftObject.otherDomesticmailTo3 : draftObject.otherForeginmailTo3,
      streetAddress: isThirdPartyRecipient ? draftObject?.otherDomesticstreetAddr1 ?? '' : draftObject?.otherForeginstreetAddr1 ?? '',
      streetAddressCont: isThirdPartyRecipient ? draftObject?.otherDomesticstreetAddr2 ?? '' : draftObject?.otherForeginstreetAddr2 ?? '',     
      city: isThirdPartyRecipient ? response.draftObject?.otherDomesticAddrCity ?? '' : draftObject?.otherForeginAddrCity ?? '',  
      state: isThirdPartyRecipient? response.draftObject?.otherDomesticAddrState : draftObject.recipient?.stateName ?? '',
      zipCode: isThirdPartyRecipient? response.draftObject?.otherDomesticAddrZipCode : draftObject.recipient?.zipCode ?? '',      
      country: isThirdPartyRecipient ? response.draftObject.recipient?.countryName ?? '' : draftObject?.otherForeginAddrCountry ?? '',
      stateProvince: '',
      postalCode: '',
      checkReason: response.draftObject.withdrawalReasonCode ?? '',
      distributionReason:  response.draftObject.distributionReasonCdValue,
      fedTaxPercent: `${draftObject.fedTaxAmount.toString()}%`,
      stateTaxPercent: `${draftObject.stateTaxAmount.toString()}%`,
    } as CheckStandingAuthDetails
  }
}


function mapJournalSloaDetailsFromInitRelease(response: DraftListAttributes, state: State): StandingAuthorizationForm {
  let draftObject = response.draftObject;
  return {
    ...state,
    transactionType: 'internal-transfer',
    isSubmitAndCreateBundledSa: response.draftObject.bsloaTransaction,
    standingAuthorizationDetails: {
      ...state.standingAuthorizationDetails,
      fedTaxPercent: draftObject.fedTaxAmount?.toString(),
      stateTaxPercent: draftObject.stateTaxAmount?.toString(),
      authorizationNickname: draftObject.nickname,
      toAccount: draftObject.toAccountId?.toString()
    } as ItStandingAuthDetails
  }
}

function mapWireSloaDetailsFromInitRelease(response: DraftListAttributes, state: State): StandingAuthorizationForm {
  let draftObject = response.draftObject;
  let intermediaryPhoneNumber = [draftObject.furCreditPhone1, draftObject.furCreditPhone2, draftObject.furCreditPhone3].filter(x => x).join('-');
  intermediaryPhoneNumber = draftObject.furCreditPhone4 ? `${intermediaryPhoneNumber} Ext${draftObject.furCreditPhone4}` : intermediaryPhoneNumber;
  let isthirdparty:boolean = draftObject.payeeType === "payeeThirdParty" || draftObject.payeeType === "payeeEscrow";
  return {
    ...state,
    transactionType: 'wire',
    isSubmitAndCreateBundledSa: response.draftObject.bSLOATxn,
    standingAuthorizationDetails: {
      ...state.standingAuthorizationDetails,
      recipient: isthirdparty ? 'thirdParty' : 'firstParty',
      isEscrow: draftObject.payeeType === "payeeEscrow",
      toAccount: draftObject.beneAccount ?? '',
      aba: draftObject.recBankAba ?? '',
      bankID: draftObject.recBankAba ?? '',
      receivingAccountHolderName1: draftObject.beneName1 ?? '',
      receivingAccountHolderName2: draftObject.beneName2 ?? '',
      receivingAccountHolderAddress1: draftObject.beneAddress1 ?? '',
      receivingAccountHolderAddress2: draftObject.beneAddress2 ?? '',
      receivingAccountHolderCountry: draftObject.beneCountry ?? '',
      countryCode: draftObject.recBankCountry ?? '',
      receivingBankName1: draftObject.recBankName1 ?? '',
      receivingBankName2: draftObject.recBankName2 ?? '',
      receivingBankAddress1: draftObject.recBankAddress1 ?? '',
      receivingBankAddress2: draftObject.recBankAddress2 ?? '',
      receivingBankCountry: draftObject.recBankCountry ?? '',
      transmissionNotes: draftObject.note ?? draftObject.Note ?? '',
      authorizationNickname: draftObject.nickname,
      fedTaxPercent: `${draftObject.fedTaxAmount}%`,
      stateTaxPercent: `${draftObject.stateTaxAmount}%`,
      intermediarySelection: draftObject.canDisplayFurCreditInfo ? 'yes' : 'no',
      intermediaryName: draftObject.furCreditName1 ?? '',
      intermediaryNameCont: draftObject.furCreditName2 ?? '',
      intermediaryAccountNumber: draftObject.furCreditAccount ?? '',
      intermediaryAddress: draftObject.furCreditAddress1 ?? '',
      intermediaryAddressCont: draftObject.furCreditAddress2 ?? '',
      intermediaryCountry: draftObject.furCountryCd ?? '',
      intermediaryPhone: draftObject.isIsoCompliant ? draftObject?.firstIntermediary?.phone ?? '' :intermediaryPhoneNumber,
      isISOCompliant: draftObject.isIsoCompliant,
      receivingAccountHolderDepartment: draftObject.beneficiaryInfo?.postalAddress.department || draftObject.recipient?.postalAddress.department || '',
      receivingAccountHolderStreetName: draftObject.beneficiaryInfo?.postalAddress.streetName || draftObject.recipient?.postalAddress.streetName || '',
      receivingAccountHolderStreetNumber: draftObject.beneficiaryInfo?.postalAddress.buildingNumber || draftObject.recipient?.postalAddress.buildingNumber || '',
      receivingAccountHolderBuildingName: draftObject.beneficiaryInfo?.postalAddress.buildingName || draftObject.recipient?.postalAddress.buildingName || '',
      receivingAccountHolderFloor: draftObject.beneficiaryInfo?.postalAddress.floor || draftObject.recipient?.postalAddress.floor || '',
      receivingAccountHolderApartmentSuiteEtc: draftObject.beneficiaryInfo?.postalAddress.room || draftObject.recipient?.postalAddress.room || '',
      receivingAccountHolderZip: draftObject.beneficiaryInfo?.postalAddress.postCode || draftObject.recipient?.postalAddress.postCode || '',
      receivingAccountHolderCity: draftObject.beneficiaryInfo?.postalAddress.townName || draftObject.recipient?.postalAddress.townName || '',
      receivingAccountHolderTownLocationName: draftObject.beneficiaryInfo?.postalAddress.townLocationName || draftObject.recipient?.postalAddress.townLocationName || '',
      receivingAccountHolderDistrict: draftObject.beneficiaryInfo?.postalAddress.districtName || draftObject.recipient?.postalAddress.districtName || '',
      receivingAccountHolderState: draftObject.beneficiaryInfo?.postalAddress.countrySubDivision || draftObject.recipient?.postalAddress.countrySubDivision || '',
      name: draftObject.beneName1,
      receivingBankCity: draftObject.beneficairyBank?.postalAddress?.townName || draftObject.recipientBank?.postalAddress?.townName || '',
      receivingBankState: draftObject.beneficairyBank?.postalAddress?.countrySubDivision || draftObject.recipientBank?.postalAddress?.countrySubDivision || '',
      intermediaryDepartment: draftObject.firstIntermediary?.postalAddress.department,
      intermediaryStreetName: draftObject.firstIntermediary?.postalAddress.streetName,
      intermediaryStreetNumber: draftObject.firstIntermediary?.postalAddress.buildingNumber,
      intermediaryBuildingName: draftObject.firstIntermediary?.postalAddress.buildingName,
      intermediaryFloor: draftObject.firstIntermediary?.postalAddress.floor,
      intermediaryApartmentSuiteEtc: draftObject.firstIntermediary?.postalAddress.room,
      intermediaryZip: draftObject.firstIntermediary?.postalAddress.postCode,
      intermediaryCity: draftObject.firstIntermediary?.postalAddress.townName,
      intermediaryState: draftObject.firstIntermediary?.postalAddress.countrySubDivision,
      taxPayerId: draftObject.taxRemittance?.taxId ?? draftObject.TaxRemittance?.taxId ?? '',
      taxTypeCode: draftObject.taxRemittance?.taxTypeCode ?? draftObject.TaxRemittance?.taxTypeCode ?? ''
    } as WireStandingAuthDetails
  }
}
